// src/lib/learn/lesson-review.ts
//
// THE QUALITY GATE — pure.
//
// Nothing on the learning path carries the "Expert-verified" mark until a human
// has passed it. Acely's reputation wound is "AI slop"; a mark that a machine
// could award itself would be worth nothing. This module holds:
//
//   - the checklist a reviewer works through (two items a machine pre-fills,
//     with its reasons, and four only a person can answer)
//   - the order lessons come up for review: the ten highest-leverage topics
//     first (the biggest banks and the most test questions), then the rest of
//     the path in path order with each step's sub-topic modules right after it
//   - the shape of a review submission and what "verified" requires
//
// The outcome lives on the Lesson row — reviewedAt / reviewedBy / reviewNotes
// (migration 20260909170000_lesson_review). Regeneration must clear reviewedAt:
// a rewritten lesson is a new lesson.
//
// Pure — the admin page, its API route and the student-facing badge import it;
// the database stays in the page and the route.

import type { SatSectionCode } from '@/lib/sat/taxonomy'
import { findStepBySlug, pathSteps } from './learning-path'
import {
  honestReadMinutes,
  isReadTimeHonest,
  lessonWordCount,
  WORDS_PER_MINUTE,
  type ReadableLessonContent,
} from './read-time'
import { exampleChoiceIssue, type ChoiceLetter, type ExampleChoice } from './worked-example-choices'

// Re-exported so callers keep importing the reading-time vocabulary from the
// review module they already use; the maths itself lives in read-time.ts, which
// the LESSON GENERATOR also calls, so a generated lesson and the check that
// judges it can never disagree.
export { honestReadMinutes, isReadTimeHonest, lessonWordCount, WORDS_PER_MINUTE }
export type { ReadableLessonContent }

/* ------------------------------------------------------------------ */
/*  The checklist                                                      */
/* ------------------------------------------------------------------ */

export type ReviewCheckId =
  | 'worked_examples'
  | 'example_choices'
  | 'pitfalls'
  | 'mini_check'
  | 'rw_why'
  | 'no_act'
  | 'read_time'

export type ReviewCheck = {
  id: ReviewCheckId
  label: string
  /** Which section the item applies to; 'ALL' for both. */
  section: 'ALL' | SatSectionCode
  /** True when a machine pre-fills the answer (the reviewer still sees why). */
  auto: boolean
}

export const REVIEW_CHECKLIST: ReviewCheck[] = [
  {
    id: 'worked_examples',
    label:
      'Worked examples are correct and SAT-shaped: a real question type, the real rules, one defensible answer.',
    section: 'ALL',
    auto: false,
  },
  {
    id: 'example_choices',
    label:
      'Every multiple-choice worked example shows its four choices before the solution, and the solution names the right one.',
    section: 'ALL',
    auto: true,
  },
  {
    id: 'pitfalls',
    label: 'Pitfalls are mistakes students actually make, not padding.',
    section: 'ALL',
    auto: false,
  },
  {
    id: 'mini_check',
    label: 'The mini-check question exists in the bank and is not archived.',
    section: 'ALL',
    auto: true,
  },
  {
    id: 'rw_why',
    label:
      'The "why" behind each answer is defensible from the text alone, never from outside knowledge.',
    section: 'RW',
    auto: false,
  },
  {
    id: 'no_act',
    label: 'No ACT-only content: no science reasoning, no ACT-specific rules or timing.',
    section: 'ALL',
    auto: false,
  },
  {
    id: 'read_time',
    label: 'The stated reading time is honest for the length.',
    section: 'ALL',
    auto: true,
  },
]

/** The items that apply to a lesson's section (an unknown section gets the shared ones). */
export function checklistFor(section: string | null | undefined): ReviewCheck[] {
  return REVIEW_CHECKLIST.filter((c) => c.section === 'ALL' || c.section === section)
}

/* ------------------------------------------------------------------ */
/*  The review order                                                   */
/* ------------------------------------------------------------------ */

/**
 * Review these first — the ten highest-leverage topics, in this order. The
 * biggest banks and the most questions per test; a bad lesson here costs the
 * most.
 */
export const REVIEW_PRIORITY_TOPICS: readonly string[] = [
  'Nonlinear Functions',
  'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
  'Command of Evidence',
  'Boundaries',
  'Equivalent Expressions',
  'Cross-Text Connections',
  'Linear Functions',
  'Probability and Conditional Probability',
  'Systems of 2 Linear Equations in 2 Variables',
  'Transitions',
]

export function isPriorityTopic(topic: string): boolean {
  return REVIEW_PRIORITY_TOPICS.includes(topic)
}

export type ReviewableLesson = { slug: string; topic: string }

/**
 * Sort key for the review queue: priority topics in REVIEW_PRIORITY_TOPICS
 * order, then every other step in path order with its sub-topic modules right
 * after it, then anything not on the path at all.
 */
export function reviewRank(lesson: ReviewableLesson): number {
  const priority = REVIEW_PRIORITY_TOPICS.indexOf(lesson.topic)
  if (priority >= 0) return priority
  const step = findStepBySlug(lesson.slug)
  if (!step) return 1_000_000
  const order = pathSteps().findIndex((s) => s.topic === step.topic)
  const moduleIndex = step.lessonSlug === lesson.slug ? 0 : step.modules.indexOf(lesson.slug) + 1
  return 1000 + order * 100 + moduleIndex
}

export function sortForReview<T extends ReviewableLesson>(lessons: T[]): T[] {
  return [...lessons].sort(
    (a, b) => reviewRank(a) - reviewRank(b) || a.topic.localeCompare(b.topic),
  )
}

/* ------------------------------------------------------------------ */
/*  The checks a machine can make                                      */
/* ------------------------------------------------------------------ */

export type LessonContentForChecks = Omit<ReadableLessonContent, 'workedExamples'> & {
  estimatedReadMinutes?: number
  miniCheck?: { questionId?: string }
  workedExamples?: Array<{
    problem?: string
    solution?: string
    insight?: string
    choices?: ExampleChoice[]
    correctChoice?: ChoiceLetter
  }>
}

export type LessonForChecks = {
  content: LessonContentForChecks
  /** The mini-check question's bank state; null when the id matches no question. */
  miniCheckQuestion: { archived: boolean } | null
}

export type AutoCheckResult = { ok: boolean; detail: string }
export type AutoChecks = Record<'mini_check' | 'read_time' | 'example_choices', AutoCheckResult>

/** "1 and 3", "1, 2 and 3". */
function listNumbers(ns: number[]): string {
  return ns.length <= 2
    ? ns.join(' and ')
    : `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`
}

export function autoChecks(lesson: LessonForChecks): AutoChecks {
  const id = lesson.content.miniCheck?.questionId
  const mini_check: AutoCheckResult = !id
    ? { ok: false, detail: 'No mini-check question is set.' }
    : !lesson.miniCheckQuestion
      ? { ok: false, detail: `Question ${id} is not in the bank.` }
      : lesson.miniCheckQuestion.archived
        ? { ok: false, detail: `Question ${id} is archived.` }
        : { ok: true, detail: `Question ${id} is live.` }

  const count = lessonWordCount(lesson.content)
  const minutes = honestReadMinutes(lesson.content)
  const stated = lesson.content.estimatedReadMinutes
  const basis = `≈${minutes} min at ${WORDS_PER_MINUTE} wpm (${count} words)`
  // A third either way is honest; "2 min" on an eight-minute lesson is not.
  const read_time: AutoCheckResult =
    typeof stated !== 'number' || stated <= 0
      ? { ok: false, detail: `No reading time stated; ${basis}.` }
      : isReadTimeHonest(stated, minutes)
        ? { ok: true, detail: `${stated} min stated; ${basis}.` }
        : { ok: false, detail: `${stated} min stated, but ${basis}.` }

  // A "Which choice…?" example with no choices on screen (the Boundaries
  // review finding) — see worked-example-choices.ts.
  const examples = lesson.content.workedExamples ?? []
  const broken = examples
    .map((e, i) => ({
      n: i + 1,
      issue: exampleChoiceIssue({
        problem: e.problem ?? '',
        solution: e.solution ?? '',
        choices: e.choices,
        correctChoice: e.correctChoice,
      }),
    }))
    .filter((x) => x.issue)
  const example_choices: AutoCheckResult =
    broken.length === 0
      ? {
          ok: true,
          detail: `${examples.length} worked examples; every multiple-choice one shows its choices.`,
        }
      : {
          ok: false,
          detail:
            broken.length === 1
              ? `Example ${broken[0].n} refers to answer choices A–D but shows none.`
              : `Examples ${listNumbers(broken.map((b) => b.n))} refer to answer choices A–D but show none.`,
        }

  return { mini_check, read_time, example_choices }
}

/* ------------------------------------------------------------------ */
/*  A review submission                                                */
/* ------------------------------------------------------------------ */

export type ReviewSubmission = {
  slug: string
  /** True to set the mark, false to clear it. Notes are saved either way. */
  verified: boolean
  checks: Partial<Record<ReviewCheckId, boolean>>
  notes: string | null
}

export const REVIEW_NOTES_MAX = 4000

const CHECK_IDS = new Set<string>(REVIEW_CHECKLIST.map((c) => c.id))

/** Validate an /api/admin/lessons/review body. Null when it is not a submission. */
export function parseReviewSubmission(body: unknown): ReviewSubmission | null {
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  if (typeof b.slug !== 'string' || !/^[a-z0-9-]+$/.test(b.slug)) return null
  if (typeof b.verified !== 'boolean') return null
  const checks: Partial<Record<ReviewCheckId, boolean>> = {}
  if (b.checks !== undefined) {
    if (!b.checks || typeof b.checks !== 'object') return null
    for (const [k, v] of Object.entries(b.checks as Record<string, unknown>)) {
      if (CHECK_IDS.has(k) && typeof v === 'boolean') checks[k as ReviewCheckId] = v
    }
  }
  let notes: string | null = null
  if (b.notes !== undefined && b.notes !== null) {
    if (typeof b.notes !== 'string') return null
    notes = b.notes.trim().slice(0, REVIEW_NOTES_MAX) || null
  }
  return { slug: b.slug, verified: b.verified, checks, notes }
}

/** The applicable checklist items not ticked — "verified" requires none. */
export function missingChecks(
  section: string | null | undefined,
  checks: Partial<Record<ReviewCheckId, boolean>>,
): ReviewCheckId[] {
  return checklistFor(section)
    .filter((c) => checks[c.id] !== true)
    .map((c) => c.id)
}

/** The mark itself: a human passed the checklist, and nothing has been regenerated since. */
export function isExpertVerified(lesson: {
  reviewedAt: Date | string | null | undefined
}): boolean {
  return !!lesson.reviewedAt
}
